import React from 'react';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Edit2, Archive, Trash2, Plus, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';

const rank = (r) => {
  if (r.status === 'Approved' && r.writing_number) return 0;
  if (['Approved', 'Active'].includes(r.status)) return 1;
  if (['Submitted', 'In_Progress', 'Carrier_Pending', 'Missing_Information', 'Pending_Release', 'Pending'].includes(r.status)) return 2;
  return 3;
};

/**
 * AgentContractsTable — dense, scannable table of every contract/appointment
 * (replaces the large carrier cards). Same row shape + handlers as
 * AgentCarrierPortfolio so all existing add / edit / delete flows are preserved.
 */
export const AgentContractsTable = ({ contracts = [], isKR = false, onAdd, onEdit, onDelete }) => {
  const rows = [...contracts].sort(
    (a, b) => (a.carrier_name || '').localeCompare(b.carrier_name || '') || rank(a) - rank(b),
  );
  const activeCount = contracts.filter((c) => ['Approved', 'Active'].includes(c.status) && c.writing_number).length;

  return (
    <div data-testid="agent-contracts-table">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-base font-heading font-semibold text-slate-900">Contracts</h3>
          <p className="text-xs text-slate-500">{activeCount} active · {contracts.length} total</p>
        </div>
        {isKR && (
          <Button size="sm" onClick={onAdd} data-testid="portfolio-add-btn">
            <Plus size={14} className="mr-1" /> Add Contract
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center text-slate-500">
          <Briefcase size={28} className="text-slate-300 mb-2" />
          <p className="text-sm font-medium">No contracts yet</p>
          <p className="text-xs">Add the agent's first carrier contract to start building their book.</p>
          {isKR && <Button size="sm" className="mt-3" onClick={onAdd}><Plus size={14} className="mr-1" />Add Contract</Button>}
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 font-semibold">Carrier</th>
                <th className="px-3 py-2 font-semibold">Product</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Level</th>
                <th className="px-3 py-2 font-semibold">Writing #</th>
                <th className="px-3 py-2 font-semibold">Appointed</th>
                {isKR && <th className="px-3 py-2 font-semibold text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.row_id} className="hover:bg-slate-50/70" data-testid={`contract-row-${r.row_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{r.carrier_name || 'Unknown'}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.contract_type_name || '—'}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} />
                    {r.status_reason && (
                      <p
                        className="text-[11px] text-slate-500 italic mt-1 max-w-[200px] leading-tight"
                        title={r.status_reason}
                        data-testid={`contract-row-reason-${r.row_id}`}
                      >
                        {r.status_reason}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.level_name || '—'}</td>
                  <td className="px-3 py-2 font-mono text-slate-600 whitespace-nowrap">{r.writing_number ? `#${r.writing_number}` : '—'}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                    {r.appointed_date ? formatDate(r.appointed_date) : (r.terminated_date ? `Term. ${formatDate(r.terminated_date)}` : '—')}
                  </td>
                  {isKR && (
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          type="button"
                          onClick={() => onEdit?.(r)}
                          className="p-1.5 rounded hover:bg-slate-100 transition-colors"
                          title="Open contract details"
                          data-testid={`portfolio-edit-${r.row_id}`}
                        >
                          <Edit2 size={14} className="text-slate-500" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete?.(r)}
                          className="p-1.5 rounded hover:bg-slate-100 transition-colors"
                          title={r.status === 'Approved' && r.writing_number ? 'Archive' : 'Delete'}
                          data-testid={`portfolio-delete-${r.row_id}`}
                        >
                          {r.status === 'Approved' && r.writing_number
                            ? <Archive size={14} className="text-amber-500" />
                            : <Trash2 size={14} className="text-red-500" />}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AgentContractsTable;
